import { useState } from "react";
import { Boxes, FolderPlus, Pencil, Trash2 } from "lucide-react";
import ApplicationFormModal from "../components/ApplicationFormModal";
import ConfirmDialog from "../components/ConfirmDialog";
import Loader from "../components/Loader";
import { useApplicationsContext } from "../context/ApplicationsContext";
import useManagedApplications from "../hooks/useManagedApplications";

function formatDate(value) {
  return new Intl.DateTimeFormat("fa-IR", { dateStyle: "medium" }).format(new Date(value));
}

export default function ManageApplications() {
  const { applications, loading, error, createApplication, updateApplication, deleteApplication } =
    useManagedApplications();
  const { reload: reloadNavApplications } = useApplicationsContext();

  const [dialog, setDialog] = useState(null); // {type: 'create'|'edit'|'delete', application}
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");

  const closeDialog = () => {
    setDialog(null);
    setDialogError("");
    setBusy(false);
  };

  const run = async (action) => {
    setBusy(true);
    setDialogError("");
    try {
      await action();
      closeDialog();
      // The sidebar lists applications too — keep it in step.
      reloadNavApplications();
    } catch (e) {
      setDialogError(e.message);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head page-head-row">
        <div>
          <h1>مدیریت اپلیکیشن‌ها</h1>
          <p>اپلیکیشن‌هایی که کاربران می‌توانند با ایجنت روی آن‌ها کار کنند.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setDialog({ type: "create" })}
        >
          <FolderPlus size={18} strokeWidth={1.9} aria-hidden="true" />
          اپلیکیشن جدید
        </button>
      </div>

      {loading && <Loader message="در حال دریافت اپلیکیشن‌ها…" />}

      {!loading && error && <div className="alert alert-error">{error}</div>}

      {!loading && !error && (
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>عنوان</th>
                <th>مسیر روی سرور</th>
                <th>تاریخ ثبت</th>
                <th className="table-actions-head">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <div className="state">
                      <div className="state-icon" aria-hidden="true">
                        <Boxes size={28} strokeWidth={1.7} />
                      </div>
                      <p>هنوز اپلیکیشنی ثبت نشده است.</p>
                    </div>
                  </td>
                </tr>
              )}

              {applications.map((application) => (
                <tr key={application.id}>
                  <td>
                    <strong>{application.title}</strong>
                  </td>
                  <td>
                    <code className="app-path">{application.path}</code>
                  </td>
                  <td className="cell-muted">{formatDate(application.created_at)}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="ویرایش"
                        onClick={() => setDialog({ type: "edit", application })}
                      >
                        <Pencil size={17} strokeWidth={1.9} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        title="حذف"
                        onClick={() => setDialog({ type: "delete", application })}
                      >
                        <Trash2 size={17} strokeWidth={1.9} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.type === "create" && (
        <ApplicationFormModal
          busy={busy}
          error={dialogError}
          onClose={closeDialog}
          onSubmit={(payload) => run(() => createApplication(payload))}
        />
      )}

      {dialog?.type === "edit" && (
        <ApplicationFormModal
          application={dialog.application}
          busy={busy}
          error={dialogError}
          onClose={closeDialog}
          onSubmit={(payload) => run(() => updateApplication(dialog.application.id, payload))}
        />
      )}

      {dialog?.type === "delete" && (
        <ConfirmDialog
          title="حذف اپلیکیشن"
          message={`اپلیکیشن «${dialog.application.title}» حذف می‌شود و دسترسی کاربران به آن از بین می‌رود. این کار قابل بازگشت نیست.`}
          busy={busy}
          error={dialogError}
          onClose={closeDialog}
          onConfirm={() => run(() => deleteApplication(dialog.application.id))}
        />
      )}
    </>
  );
}
